'use client';

import { createORPCClient } from '@orpc/client';
import { RPCLink } from '@orpc/client/fetch';
import type { RouterClient } from '@orpc/server';
import { createTanstackQueryUtils } from '@orpc/tanstack-query';
import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { type appRouter } from 'server/api/root';
import { toast } from 'ui';
import { isEnvDev } from 'utils/env';
import { getUrl } from './shared';

// Create the base oRPC client
const client: RouterClient<typeof appRouter> = createORPCClient(
  new RPCLink({
    url: getUrl(),
    headers: () => ({
      'x-orpc-source': 'react',
    }),
    // Logger for development
    fetch: async (url, options) => {
      if (isEnvDev) {
        console.log('[oRPC] Request:', url, options);
      }
      const response = await fetch(url, options);
      if (isEnvDev && !response.ok) {
        console.error('[oRPC] Error response:', response);
      }
      return response;
    },
  }),
);

// Create a singleton utils instance for the app
export const api = createTanstackQueryUtils(client);

function makeQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // With SSR, we usually want to set some default staleTime
        // above 0 to avoid refetching immediately on the client
        staleTime: 30 * 1000,
      },
    },
    mutationCache: new MutationCache({
      onSuccess: (_data, _vars, _mutRes, _mut, context) => {
        // Invalidate all queries after a mutation automatically
        // MutationCache.onSuccess > defaultOptions.mutations.onSuccess
        // because defaultOptions will be overridden
        context.client.invalidateQueries();
      },
    }),
    queryCache: new QueryCache({
      onError: (error) =>
        toast.error(`Something Went Wrong`, {
          description: error.message,
        }),
    }),
  });
}

let browserQueryClient: QueryClient | undefined = undefined;
let queryClient: QueryClient;

function getQueryClient() {
  if (typeof window === 'undefined') {
    // Server: always make a new query client
    queryClient = makeQueryClient();
    return queryClient;
  } else {
    // Browser: make a new query client if we don't already have one
    // This is very important, so we don't re-make a new client if React
    // suspends during the initial render. This may not be needed if we
    // have a suspense boundary BELOW the creation of the query client
    if (!browserQueryClient) {
      browserQueryClient = makeQueryClient();
      queryClient = browserQueryClient;
    }
    return browserQueryClient;
  }
}

export function ORPCReactProvider(props: {
  children: React.ReactNode;
  cookies: string;
  pathPrefix?: string;
}) {
  const queryClient = getQueryClient();

  return (
    <QueryClientProvider client={queryClient}>
      {props.children}
    </QueryClientProvider>
  );
}

// Error helper
export function isORPCClientError(cause: unknown): cause is Error {
  return cause instanceof Error;
}
